import { editorContextStore } from "@/contexts/editor-context";
import {
  StripeSubscriptionProductId,
  subscriptionPlanToRecommendedPlan,
  subscriptionPlanToRecommendedPlans,
} from "@/core/common/types/subscription";
import {
  getHighestUserStripeSubscriptionItem,
  getHighestUserStripeSubscriptionPlan,
} from "@/core/common/types/user-stripe-subscriptions";
import React from "react";

export function useUserStripeSubscriptionUpdateEffect() {
  const user = editorContextStore((state) => state.user);
  const backend = editorContextStore((state) => state.backend);
  const currentTeamId = editorContextStore((state) => state.currentTeamId);
  const publicUserId = editorContextStore((state) => state.publicUserId);
  const setUserStripeSubscriptions = editorContextStore(
    (state) => state.setUserStripeSubscriptions,
  );

  React.useEffect(() => {
    if (!backend || !publicUserId || !currentTeamId) {
      return;
    }
    //enforce getting subscriptions for the current team, not necessarily the user
    return backend.onUserSubscriptionUpdate({
      publicUserId,
      currentTeamId,
      callback: (stripeSubscriptions = []) => {
        setUserStripeSubscriptions(stripeSubscriptions);
      },
    });
  }, [user, backend, setUserStripeSubscriptions, publicUserId, currentTeamId]);
}

export function useUserHighestStripeSubscriptionItem({
  excludePlans,
}: {
  excludePlans?: StripeSubscriptionProductId[];
} = {}) {
  const userStripeSubscriptions = editorContextStore((state) => state.userStripeSubscriptions);
  return React.useMemo(() => {
    return getHighestUserStripeSubscriptionItem({
      userStripeSubscriptions,
      excludePlans,
    });
  }, [userStripeSubscriptions, excludePlans]);
}

export function useUserHighestStripeSubscription({
  excludePlans,
}: {
  excludePlans?: StripeSubscriptionProductId[];
} = {}) {
  const userStripeSubscriptions = editorContextStore((state) => state.userStripeSubscriptions);

  return React.useMemo(() => {
    return getHighestUserStripeSubscriptionPlan({
      userStripeSubscriptions,
      excludePlans,
    });
  }, [userStripeSubscriptions, excludePlans]);
}

export function useUserRecommendedSubscriptionPlans() {
  const userSubscriptionPlan = useUserHighestStripeSubscription();
  return React.useMemo(
    () => subscriptionPlanToRecommendedPlans[userSubscriptionPlan],
    [userSubscriptionPlan],
  );
}

export function useUserRecommendedSubscriptionPlan() {
  const userSubscriptionPlan = useUserHighestStripeSubscription();
  return React.useMemo(
    () => subscriptionPlanToRecommendedPlan[userSubscriptionPlan],
    [userSubscriptionPlan],
  );
}
